// Components/Common/PriceViewComponent.tsx
import React, { useState, useContext } from 'react';
import { Badge } from 'reactstrap';
// import { ServiceContext } from 'context/ServiceContextProvider';

// Utility function to check permissions
// Import your actual permission checking function
// import { IsValidPermissions } from 'helpers/permission_helper';
import { IsValidPermissions } from 'Routes/AuthProtected';

interface PriceViewComponentProps {
  price: number | string | null;
  unitPrice?: number | string | null;
  currency?: string;
  className?: string;
  showPerUnit?: boolean;
  quantity?: number | string | null;
  size?: 'sm' | 'md' | 'lg';
  requiredPermissions?: string[];
  hideIfNoPermission?: boolean; // If true, shows nothing instead of "Restricted"
}

interface PriceViewCompactProps {
  price: number | string | null;
  currency?: string;
  requiredPermissions?: string[];
}

// Main PriceViewComponent
export const PriceViewComponent: React.FC<PriceViewComponentProps> = ({ 
  price, 
  unitPrice = null,
  currency = "$", 
  className = "",
  showPerUnit = false,
  quantity = null,
  size = "md",
  requiredPermissions = ["VIEW_ALL_PURCHASE_COST"],
  hideIfNoPermission = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Get context and check permissions
  // const context = useContext(ServiceContext);
//   const { IsValidPermissions } = context;
  
  // For now, using a placeholder - replace with your actual permission check
  const hasPermission = IsValidPermissions(requiredPermissions); // Replace with: IsValidPermissions(requiredPermissions)

  // If no permission and should hide
  if (!hasPermission && hideIfNoPermission) {
    return null;
  }

  // If no permission, show restricted badge
  if (!hasPermission) {
    return (
      <span className={`text-muted fst-italic ${className}`}>
        <i className="ri-lock-line me-1"></i>
        Restricted
      </span>
    );
  }

  // Format price
  const formatPrice = (value: number | string | null): string => {
    if (value === null || value === undefined || value === '') return "0.00";
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  // Size configurations
  const sizeConfig = {
    sm: { price: "fs-14", icon: "fs-16", label: "fs-11" },
    md: { price: "fs-16", icon: "fs-18", label: "fs-12" },
    lg: { price: "fs-18", icon: "fs-20", label: "fs-13" }
  };

  const sizes = sizeConfig[size] || sizeConfig.md;

  // Prefer backend-computed unit price; fallback to client calculation only if needed.
  const calculatePerUnit = (): string => {
    if (unitPrice !== null && unitPrice !== undefined && unitPrice !== "") {
      return formatPrice(unitPrice);
    }
    if (!quantity) return "0.00";
    const priceNum = parseFloat(price?.toString() || "0");
    const qtyNum = parseFloat(quantity?.toString() || "1");
    return (priceNum / qtyNum).toFixed(2);
  };

  return (
    <div className={`d-inline-flex align-items-center gap-2 ${className}`}>
      {isVisible ? (
        <>
          <div className="d-flex flex-column">
            <span className={`fw-bold text-success ${sizes.price}`}>
              {currency}{formatPrice(price)}
            </span>
            {showPerUnit && quantity && (
              <div className={`text-muted ${sizes.label}`}>
                {currency}{calculatePerUnit()} per unit
              </div>
            )}
          </div>
          <button
            className="btn btn-sm btn-ghost-primary p-1 border-0"
            onClick={() => setIsVisible(false)}
            style={{ lineHeight: 1, cursor: 'pointer' }}
            title="Hide price"
          >
            <i className={`ri-eye-line ${sizes.icon}`}></i>
          </button>
        </>
      ) : (
        <>
          <span className={`text-muted ${sizes.price}`}>••••••</span>
          <button
            className="btn btn-sm btn-ghost-secondary p-1 border-0"
            onClick={() => setIsVisible(true)}
            style={{ lineHeight: 1, cursor: 'pointer' }}
            title="Show price"
          >
            <i className={`ri-eye-off-line ${sizes.icon}`}></i>
          </button>
        </>
      )}
    </div>
  );
};

// Compact version for table cells
export const PriceViewCompact: React.FC<PriceViewCompactProps> = ({ 
  price, 
  currency = "$",
  requiredPermissions = ["VIEW_ALL_PURCHASE_COST"]
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Get context and check permissions
  // const context = useContext(ServiceContext);
  // const { IsValidPermissions } = context;
  
  // For now, using a placeholder - replace with your actual permission check
  const hasPermission = IsValidPermissions(requiredPermissions); // Replace with: IsValidPermissions(requiredPermissions)

  if (!hasPermission) {
    return (
      <Badge color="secondary" className="fs-11">
        <i className="ri-eye-off-line"></i> ******
      </Badge>
    );
  }

  const formatPrice = (value: number | string | null): string => {
    if (value === null || value === undefined || value === '') return "0.00";
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  return (
    <div className="text-center">
      {isVisible ? (
        <div className="d-inline-flex align-items-center gap-1">
          <span className="fw-bold text-success">
            {currency}{formatPrice(price)}
          </span>
          <i 
            className="ri-eye-line fs-14 text-primary"
            onClick={toggleVisibility}
            style={{ cursor: 'pointer' }}
            title="Hide price"
          ></i>
        </div>
      ) : (
        <div className="d-inline-flex align-items-center gap-1">
          <span className="text-muted">••••</span>
          <i 
            className="ri-eye-off-line fs-14 text-muted"
            onClick={toggleVisibility}
            style={{ cursor: 'pointer' }}
            title="Show price"
          ></i>
        </div>
      )}
    </div>
  );
};

// Helper hook for managing multiple price visibility states
export const usePriceVisibility = () => {
  const [visiblePrices, setVisiblePrices] = useState<Set<string>>(new Set());

  const togglePrice = (id: string) => {
    setVisiblePrices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const isPriceVisible = (id: string) => visiblePrices.has(id);

  const showAllPrices = () => {
    // Implementation depends on your data structure
  };

  const hideAllPrices = () => {
    setVisiblePrices(new Set());
  };

  return {
    togglePrice,
    isPriceVisible,
    showAllPrices,
    hideAllPrices
  };
};

export default PriceViewComponent;